import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';

export interface DatePickerDayCell {
  date: Date | null;
  day: number | null;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

const WEEKS_SHOWN = 6;
const CELLS_SHOWN = WEEKS_SHOWN * 7;
const DROPUP_THRESHOLD_PX = 320;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Shared date-field-plus-popup-calendar — a trigger styled like a text input
 * that opens a small month calendar. Used wherever a plain "pick a date"
 * field is needed (e.g. Storybook memory date).
 *
 * The calendar grid always renders a fixed 6 rows (42 cells), padding short
 * months with empty trailing cells instead of only as many rows as that
 * month needs — otherwise the popup's height changes as you navigate
 * between months (e.g. a 4-row February vs a 6-row month with a Sunday
 * start), which makes the prev/next arrows feel like they're relocating
 * under the pointer instead of just changing the month.
 *
 * Usage: <app-date-picker [(value)]="memoryDate" placeholder="Select date" />
 * `value` / `valueChange` carry a 'yyyy-MM-dd' string, or '' when unset.
 */
@Component({
  selector: 'app-date-picker',
  standalone: true,
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePicker {
  @Input() value = '';
  @Output() readonly valueChange = new EventEmitter<string>();
  @Input() placeholder = 'Select date';
  @Input() invalid = false;
  /** Optional 'yyyy-MM-dd' floor — dates before it render disabled. */
  @Input() minDate = '';

  readonly isOpen = signal(false);
  readonly dropUp = signal(false);
  readonly viewYear = signal(new Date().getFullYear());
  readonly viewMonth = signal(new Date().getMonth());

  readonly monthLabel = computed(() => `${MONTH_NAMES[this.viewMonth()]} ${this.viewYear()}`);

  readonly display = computed(() => {
    const val = this.value;
    if (!val) return '';
    const parts = val.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return val;
    return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
  });

  readonly calendarDays = computed<DatePickerDayCell[]>(() => {
    const year = this.viewYear();
    const month = this.viewMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selVal = this.value;
    const selected = selVal ? new Date(selVal + 'T00:00:00') : null;
    const min = this.minDate ? new Date(this.minDate + 'T00:00:00') : null;

    const emptyCell = (): DatePickerDayCell => ({
      date: null,
      day: null,
      isCurrentMonth: false,
      isToday: false,
      isSelected: false,
      isDisabled: true,
    });

    const cells: DatePickerDayCell[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(emptyCell());
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      cells.push({
        date,
        day: d,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isSelected: selected ? date.getTime() === selected.getTime() : false,
        isDisabled: min ? date < min : false,
      });
    }
    // Always pad to a full 6 rows (42 cells) — see class doc comment.
    while (cells.length < CELLS_SHOWN) {
      cells.push(emptyCell());
    }
    return cells;
  });

  open(event: MouseEvent): void {
    if (this.value) {
      const [y, m] = this.value.split('-').map(Number);
      this.viewYear.set(y);
      this.viewMonth.set(m - 1);
    } else {
      const now = new Date();
      this.viewYear.set(now.getFullYear());
      this.viewMonth.set(now.getMonth());
    }
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.dropUp.set(spaceBelow < DROPUP_THRESHOLD_PX && rect.top > DROPUP_THRESHOLD_PX);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  select(date: Date | null): void {
    if (!date) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    this.valueChange.emit(`${y}-${m}-${d}`);
    this.isOpen.set(false);
  }

  prevMonth(): void {
    if (this.viewMonth() === 0) {
      this.viewMonth.set(11);
      this.viewYear.update((y) => y - 1);
    } else {
      this.viewMonth.update((v) => v - 1);
    }
  }

  nextMonth(): void {
    if (this.viewMonth() === 11) {
      this.viewMonth.set(0);
      this.viewYear.update((y) => y + 1);
    } else {
      this.viewMonth.update((v) => v + 1);
    }
  }
}
